// src/components/ImageCropUpload.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import "../styles/Admin.css";

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.src = url;
  });
}

async function getCroppedBlob(imageSrc, cropPixels) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported.");

  canvas.width = cropPixels.width;
  canvas.height = cropPixels.height;

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    cropPixels.width,
    cropPixels.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9);
  });
}

async function readResponse(res) {
  // Some backends return HTML for errors; be defensive.
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function buildAuthHeaders(token) {
  const h = {};
  if (!token) return h;
  h.Authorization = `Bearer ${token}`;
  h["x-admin-token"] = token;
  return h;
}

/**
 * Props:
 * - token: admin token string
 * - value: current URL (image/pdf)
 * - onChange(url)
 * - folder: upload folder name
 * - label: field label
 * - aspect: crop aspect ratio (images)
 * - isPdf: if true, uploads a PDF without cropping
 * - onAuthError: optional callback invoked on 401
 */
export default function ImageCropUpload({
  token,
  value,
  onChange,
  folder = "general",
  label = "Upload",
  aspect = 1,
  isPdf = false,
  onAuthError,
}) {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const fileInputRef = useRef(null);

  // Clean up object URLs
  useEffect(() => {
    return () => {
      if (src && src.startsWith("blob:")) URL.revokeObjectURL(src);
    };
  }, [src]);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedPixels(croppedAreaPixels);
  }, []);

  const pickFile = () => fileInputRef.current?.click();

  const handleImageFile = (file) => {
    if (!file) return;
    setErr("");
    const url = URL.createObjectURL(file);
    setSrc(url);
    setOpen(true);
  };

  const handlePdfFile = (file) => {
    if (!file) return;
    setErr("");
    uploadPdf(file).catch(() => {});
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (isPdf) handlePdfFile(f);
    else handleImageFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    if (isPdf) handlePdfFile(f);
    else handleImageFile(f);
  };

  const doUpload = async () => {
    try {
      setErr("");
      if (!token) throw new Error("Authentication token is missing. Please log in.");
      if (!src || !croppedPixels) throw new Error("No image selected or cropped.");

      setBusy(true);

      const blob = await getCroppedBlob(src, croppedPixels);
      if (!blob) throw new Error("Could not create cropped image.");

      const fd = new FormData();
      fd.append("file", blob, "upload.jpg");
      // Some backends expect token in fields for multipart forms
      fd.append("token", token);

      const baseURL = process.env.REACT_APP_API_URL || "";
      const uploadUrl = `${baseURL}/api/upload?folder=${encodeURIComponent(folder)}`;

      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: buildAuthHeaders(token),
        body: fd,
        credentials: "include",
      });

      const json = await readResponse(res);
      if (!res.ok) {
        if (res.status === 401 && typeof onAuthError === "function") onAuthError();
        console.error("Upload response error:", json);
        throw new Error(json?.error || json?.message || "Upload failed. Please check token and file type.");
      }

      // Backends commonly return { url }
      const url = json?.url || json?.data?.url;
      if (!url) throw new Error("Upload succeeded but no URL was returned by the server.");

      onChange(url);
      setOpen(false);
    } catch (e) {
      setErr(e.message || "Upload failed");
      console.error("Upload error:", e);
    } finally {
      setBusy(false);
    }
  };

  const uploadPdf = async (file) => {
    try {
      setErr("");
      if (!token) throw new Error("Authentication token is missing. Please log in.");
      if (!file) throw new Error("No file selected.");

      setBusy(true);

      const fd = new FormData();
      fd.append("file", file, file.name);
      fd.append("token", token);

      const baseURL = process.env.REACT_APP_API_URL || "";
      const uploadUrl = `${baseURL}/api/upload?folder=${encodeURIComponent(folder)}`;

      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: buildAuthHeaders(token),
        body: fd,
        credentials: "include",
      });

      const json = await readResponse(res);
      if (!res.ok) {
        if (res.status === 401 && typeof onAuthError === "function") onAuthError();
        console.error("PDF Upload response error:", json);
        throw new Error(json?.error || json?.message || "PDF upload failed. Please check token.");
      }

      const url = json?.url || json?.data?.url;
      if (!url) throw new Error("Upload succeeded but no URL was returned by the server.");

      onChange(url);
    } catch (e) {
      setErr(e.message || "PDF upload failed");
      console.error("PDF Upload error:", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>{label}</label>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        style={{
          border: "1px dashed rgba(0,0,0,0.25)",
          padding: 12,
          borderRadius: 10,
          background: "rgba(0,0,0,0.02)",
        }}
      >
        {value ? (
          isPdf ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <a href={value} target="_blank" rel="noreferrer">
                View PDF
              </a>
              <button type="button" className="admin-btn-secondary" onClick={pickFile} disabled={busy}>
                Replace PDF
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <img
                src={value}
                alt="uploaded"
                style={{ width: 140, height: 90, objectFit: "cover", borderRadius: 10 }}
              />
              <button type="button" className="admin-btn-secondary" onClick={pickFile} disabled={busy}>
                Replace Image
              </button>
            </div>
          )
        ) : (
          <button type="button" className="admin-btn-primary" onClick={pickFile} disabled={busy}>
            {busy ? "Uploading..." : isPdf ? "Upload PDF" : "Upload Image"}
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={isPdf ? "application/pdf" : "image/*"}
          style={{ display: "none" }}
          onChange={onFileChange}
        />
      </div>

      {err ? <div style={{ color: "crimson", marginTop: 8 }}>{err}</div> : null}

      {!isPdf && open ? (
        <div style={{ marginTop: 12 }}>
          <div
            style={{
              position: "relative",
              width: "100%",
              height: 320,
              background: "#111",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          <div className="admin-toolbar" style={{ marginTop: 10 }}>
            <div className="admin-actions">
              <button type="button" className="admin-btn-primary" onClick={doUpload} disabled={busy}>
                {busy ? "Uploading..." : "Upload"}
              </button>
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={() => setOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
